import { useCallback, useEffect, useRef, useState } from "react";
import type { AoeTemplate, ServerMessage } from "@roll1/shared";
import { getAoePolygon } from "@roll1/shared/aoe-geometry";

export interface UseAoeTemplatesReturn {
  templates: Map<string, AoeTemplate>;
  affectedTokens: Map<string, string[]>;
  placeTemplate: (template: Omit<AoeTemplate, 'id' | 'placedBy'>, durationMs?: number) => void;
  removeTemplate: (templateId: string) => void;
  removeAllTemplates: () => void;
  handleTemplateMessage: (msg: ServerMessage) => void;
}

/**
 * React hook for AoE template state management and canvas rendering.
 * Manages ephemeral AoE templates received via WS.
 */
export function useAoeTemplates(
  send: (msg: Record<string, unknown>) => void,
  activePageId: string,
  markDirty?: (layer: 'map' | 'grid' | 'token' | 'drawing' | 'fog' | 'ui') => void,
): UseAoeTemplatesReturn {
  const [templates, setTemplates] = useState<Map<string, AoeTemplate>>(new Map());
  const [affectedTokens, setAffectedTokens] = useState<Map<string, string[]>>(new Map());
  const autoRemoveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Clear all templates on page switch
  useEffect(() => {
    setTemplates(new Map());
    setAffectedTokens(new Map());
    // Clear timers
    for (const timer of autoRemoveTimers.current.values()) {
      clearTimeout(timer);
    }
    autoRemoveTimers.current.clear();
    markDirty?.('ui');
    markDirty?.('token');
  }, [activePageId, markDirty]);

  const handleTemplateMessage = useCallback((msg: ServerMessage) => {
    if ((msg.type === 'template:placed' || msg.type === 'template:removed') && msg.pageId !== undefined && msg.pageId !== activePageId) return;
    if (msg.type === 'template:placed') {
      const { template, affectedTokenIds } = msg;
      setTemplates(prev => {
        const next = new Map(prev);
        next.set(template.id, template);
        return next;
      });
      setAffectedTokens(prev => {
        const next = new Map(prev);
        next.set(template.id, affectedTokenIds);
        return next;
      });
      markDirty?.('ui');
      markDirty?.('token');
    } else if (msg.type === 'template:removed') {
      const { templateId } = msg;
      setTemplates(prev => {
        const next = new Map(prev);
        next.delete(templateId);
        return next;
      });
      setAffectedTokens(prev => {
        const next = new Map(prev);
        next.delete(templateId);
        return next;
      });
      // Clear auto-remove timer if exists
      const timer = autoRemoveTimers.current.get(templateId);
      if (timer) {
        clearTimeout(timer);
        autoRemoveTimers.current.delete(templateId);
      }
      markDirty?.('ui');
      markDirty?.('token');
    }
  }, [markDirty, activePageId]);

  const removeTemplate = useCallback((templateId: string) => {
    send({
      id: crypto.randomUUID(),
      type: 'template:remove',
      timestamp: new Date().toISOString(),
      templateId,
      pageId: activePageId,
    });
  }, [send, activePageId]);

  const placeTemplate = useCallback((partial: Omit<AoeTemplate, 'id' | 'placedBy'>, durationMs?: number) => {
    const templateId = crypto.randomUUID();
    send({
      id: crypto.randomUUID(),
      type: 'template:place',
      timestamp: new Date().toISOString(),
      templateId,
      shape: partial.shape,
      originX: partial.originX,
      originY: partial.originY,
      sizeFt: partial.sizeFt,
      rotation: partial.rotation,
      color: partial.color,
      label: partial.label,
      pageId: activePageId,
    });

    // Client-side auto-remove timer
    if (durationMs && durationMs > 0) {
      const timer = setTimeout(() => {
        removeTemplate(templateId);
        autoRemoveTimers.current.delete(templateId);
      }, durationMs);
      autoRemoveTimers.current.set(templateId, timer);
    }
  }, [send, removeTemplate, activePageId]);

  const removeAllTemplates = useCallback(() => {
    for (const templateId of templates.keys()) {
      removeTemplate(templateId);
    }
  }, [templates, removeTemplate]);

  return {
    templates,
    affectedTokens,
    placeTemplate,
    removeTemplate,
    removeAllTemplates,
    handleTemplateMessage,
  };
}

/**
 * Render AoE templates on a canvas 2D context (UI layer).
 * Call this from the UI layer render callback.
 */
export function renderAoeTemplates(
  ctx: CanvasRenderingContext2D,
  templates: Map<string, AoeTemplate>,
  gridSize: number,
  gridSizeFt: number,
): void {
  for (const template of templates.values()) {
    const polygon = getAoePolygon(template, gridSize, gridSizeFt);
    if (polygon.length === 0) continue;

    ctx.save();

    // Draw fill
    ctx.beginPath();
    ctx.moveTo(polygon[0].x, polygon[0].y);
    for (let i = 1; i < polygon.length; i++) {
      ctx.lineTo(polygon[i].x, polygon[i].y);
    }
    ctx.closePath();

    // Parse color and set alpha
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = template.color;
    ctx.fill();

    // Draw stroke
    ctx.globalAlpha = 0.6;
    ctx.strokeStyle = template.color;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw label if present
    if (template.label) {
      ctx.globalAlpha = 1;
      ctx.fillStyle = template.color;
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';

      // Calculate center of bounding box for label position
      let cx = 0, cy = 0;
      for (const p of polygon) {
        cx += p.x;
        cy += p.y;
      }
      cx /= polygon.length;
      cy /= polygon.length;

      ctx.fillText(template.label, cx, cy - 10);
    }

    ctx.restore();
  }
}
